// Pénz-formázás — az Odin/BFF Decimal→string értékeit jeleníti meg magyar (locale-tudatos) formában.
// FONTOS: a UI SOHA nem számol/kerekít float-tal — a backend adta (már kerekített) értéket CSAK formázzuk.
// Konvenció (a legacy CRM + a service-catalog 1:1): HUF → egész-forint (0 tizedes), minden más deviza → 2
// tizedes; mindig ezres-tagolással + a devizakóddal a szám UTÁN (pl. „127 000 HUF", „318,14 EUR"). A HUF a
// legacy pénz-logikája szerint már egész forintra kerekítve érkezik (a törtrész .00) — itt csak elrejtjük.
// A kiíráshoz `tabular-nums` + jobbra-igazítás javasolt a hívó cellában (fix-szélességű számjegyek).

const EM_DASH = '—';

/**
 * Egyetlen pénz-érték formázása. A `value` a backend Decimal→string alakja (a pontosság megőrzéséért string).
 * Üres/érvénytelen érték → „—". A `currency` ISO-4217 kód (HUF | EUR | USD …) dönti el a tizedesek számát.
 */
export function formatMoney(
  value: string | null | undefined,
  currency: string,
  locale = 'hu-HU',
): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return EM_DASH;
  const fractionDigits = currency === 'HUF' ? 0 : 2;
  const num = n.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${num} ${currency}`;
}

/**
 * Rövidített (kompakt) pénz-formázás — nagy összegek tömör kiírására (pl. chart-tengely, donut-közép):
 * „30,7 M HUF" (hu) / „30.7M HUF" (en). Az `Intl` `notation: 'compact'`-ját használja (locale-tudatos
 * rövidítés: e/M/Mrd ill. k/M/B), max 1 tizedes. A `value` a backend Decimal→string alakja; üres/érvénytelen → „—".
 * FIGYELEM: kizárólag MEGJELENÍTÉSRE (kerekít) — összeg-számításra sose ezt használd.
 */
export function formatMoneyShort(
  value: string | null | undefined,
  currency: string,
  locale = 'hu-HU',
): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return EM_DASH;
  const num = n.toLocaleString(locale, { notation: 'compact', maximumFractionDigits: 1 });
  return `${num} ${currency}`;
}

/** Ár-/összeg-tartomány: „min – max deviza"; ha min=max, egyetlen érték; hiányzó adat → „—". */
export function formatMoneyRange(
  min: string | null | undefined,
  max: string | null | undefined,
  currency: string | null | undefined,
  locale = 'hu-HU',
): string {
  if (min === null || min === undefined || max === null || max === undefined || !currency) {
    return EM_DASH;
  }
  if (min === max) return formatMoney(min, currency, locale);
  return `${formatMoney(min, currency, locale)} – ${formatMoney(max, currency, locale)}`;
}

/** Deviza-SZIMBÓLUMos ár-formázás a katalógus árazás-cellájához (a legacy `FormatterService::formatPrice` 1:1):
 *  hu → „2 000 Ft" (HUF: 0 tizedes ha egész, szimbólum a szám UTÁN) / „$1 234,89" / „€1 234,89" (szimbólum ELŐL, 2 tizedes);
 *  en → ISO-kód elöl, angol szám: „HUF 1,234" / „USD 1,234.89". Üres/érvénytelen → „—". A `locale` a UI nyelve (`hu`|`en`). */
const CURRENCY_SYMBOL: Record<string, string> = { HUF: 'Ft', USD: '$', EUR: '€' };
export function formatPriceSymbol(
  value: string | null | undefined,
  currency: string,
  locale = 'hu',
): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const n = Number(value);
  if (Number.isNaN(n)) return EM_DASH;
  const isHuf = currency === 'HUF';
  const decimals = isHuf && Number.isInteger(n) ? 0 : 2;
  if (locale === 'en') {
    const num = n.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${currency} ${num}`;
  }
  const num = n.toLocaleString('hu-HU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  return isHuf ? `${num} ${symbol}` : `${symbol}${num}`;
}
